import type { ReactNode } from 'react';

interface ComparisonHoverTooltipProps {
  content: ReactNode;
  children: ReactNode;
  className?: string;
  /** Show tooltip below trigger (avoids clipping on first table rows). */
  placement?: 'top' | 'bottom';
  /** Fill cell width for column-wide hover targets. */
  block?: boolean;
  /** Wider tooltip body (shared-holdings comparison tables). */
  wide?: boolean;
  /** Pin the tooltip to the right edge of the trigger. */
  alignEnd?: boolean;
}

const ComparisonHoverTooltip = ({
  content,
  children,
  className = '',
  placement = 'top',
  block = false,
  wide = false,
  alignEnd = false,
}: ComparisonHoverTooltipProps) => {
  const Tag = block ? 'div' : 'span';

  const wrapClass = [
    'group relative cursor-help',
    block ? 'block w-full max-w-none' : 'inline-flex max-w-full',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const tooltipClass = [
    'pointer-events-none absolute z-50 rounded-[8px] border border-white/[0.14]',
    'bg-[color:rgba(13,17,30,0.98)] px-3 py-2 text-[13px] whitespace-normal',
    'shadow-[0_8px_24px_rgba(0,0,0,0.45)] invisible opacity-0',
    'transition-[opacity,transform,visibility] duration-150',
    'group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100',
    wide
      ? 'min-w-[min(92vw,680px)] max-w-[min(92vw,760px)]'
      : 'min-w-[220px] max-w-[320px]',
    alignEnd ? 'left-auto right-0' : 'left-0',
    placement === 'bottom'
      ? 'top-[calc(100%+8px)] -translate-y-1 group-hover:translate-y-0 group-focus-within:translate-y-0'
      : 'bottom-[calc(100%+8px)] translate-y-1 group-hover:translate-y-0 group-focus-within:translate-y-0',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag className={wrapClass}>
      {children}
      <span className={tooltipClass} role="tooltip">
        {content}
      </span>
    </Tag>
  );
};

export default ComparisonHoverTooltip;
